import { useState } from "react";
import { Label } from "@/components/ui/label";
import { UnifiedMarkdownRenderer } from "@/components/ui/unified-markdown-renderer";
import { Code } from "lucide-react";
import { generateTemplatePrompt } from "@/integrations/templates/templateService";
import { useNotifications } from "@/contexts/NotificationContext";
import { User } from "@supabase/supabase-js";
import AIHelperButton from "./AIHelperButton";
import { useTranslation } from "react-i18next";

interface TemplateAdvancedSettingsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  isMobile: boolean;
  user?: User | null;
  canEdit?: boolean;
  forceUpdateKey?: string | number;
}

const TemplateAdvancedSettings = ({
  prompt,
  setPrompt,
  isMobile,
  user,
  canEdit = true,
  forceUpdateKey,
}: TemplateAdvancedSettingsProps) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const { addNotification } = useNotifications();

  const handlePromptGeneration = async (userInput: string) => {
    if (!userInput.trim()) return;

    setIsGenerating(true);
    try {
      const generatedPrompt = await generateTemplatePrompt(userInput);
      setPrompt(generatedPrompt);
      setIsHelperOpen(false);

      addNotification({
        userId: user?.id || "guest",
        type: "success",
        message: t('templates.advanced.gen_success'),
      });
    } catch (error) {
      console.error("Ошибка при генерации настроек:", error);
      addNotification({
        userId: user?.id || "guest",
        type: "error",
        message: error instanceof Error ? error.message : t('templates.advanced.gen_error'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="prompt" className="flex items-center gap-2 text-base font-medium">
          <Code size={16} className="text-muted-foreground" />
          {t('templates.advanced.prompt_label')}
        </Label>

        {canEdit && (
          <AIHelperButton
            isMobile={isMobile}
            isOpen={isHelperOpen}
            onOpenChange={setIsHelperOpen}
            onGenerate={handlePromptGeneration}
            isGenerating={isGenerating}
          />
        )}
      </div>
      <div className="w-full">
        <UnifiedMarkdownRenderer
          value={prompt}
          onChange={setPrompt}
          placeholder={t('templates.advanced.prompt_placeholder')}
          mode={canEdit ? "edit" : "view"}
          height={200}
          className="w-full"
          forceUpdateKey={forceUpdateKey}
        />
      </div>
    </div>
  );
};

export default TemplateAdvancedSettings;